import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Spin } from '@jjj/components';
import { clearLastVisitedRoute } from 'src/helpers/utility';
import { IOrganization, RoleId, ROLE_IDS } from '../../../../redux/orgSwitcher/types';
import { t } from '../../../../i18n';
import { getOndemandUrl, getAgencyUrl } from '../../../../settings';
import { getUnifiedLoginUrlForRole, isSafeguardDisabled } from '../utils';
import {
  OrgsContainer,
  OrgItem,
  OrgHeader,
  OrgLogo,
  OrgName,
  ChevronIcon,
  RolesContainer,
  RoleItem,
  RoleName,
  RoleStatus,
  LoadingContainer,
} from '../OrgSwitcher.style';

interface IOrgListProps {
  organizations: IOrganization[];
  loading: boolean;
  userEmail?: string;
  currentOrgId?: string;
  isOpen: boolean;
  onShowSwitchModal: (orgId: string, roleId: RoleId) => void;
  onClose: () => void;
}

const OrgList: React.FC<IOrgListProps> = ({
  organizations,
  loading,
  userEmail,
  currentOrgId,
  isOpen,
  onShowSwitchModal,
  onClose,
}) => {
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(currentOrgId || null);

  // Reset expanded state to current org when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setExpandedOrgId(currentOrgId || null);
    }
  }, [isOpen, currentOrgId]);

  // Sort organizations: current org first, then the rest
  const sortedOrganizations = useMemo(() => {
    if (!currentOrgId) return organizations;

    return [...organizations].sort((a, b) => {
      if (a.id === currentOrgId) return -1;

      if (b.id === currentOrgId) return 1;

      return 0;
    });
  }, [organizations, currentOrgId]);

  const handleToggleExpand = useCallback((orgId: string) => {
    setExpandedOrgId(prev => (prev === orgId ? null : orgId));
  }, []);

  const getRoleSwitchUrl = useCallback(
    (org: IOrganization, roleId: RoleId): string | null => {
      const isSameOrg = org.id === currentOrgId;

      if (isSameOrg && roleId === ROLE_IDS.admin) {
        return null;
      }

      if (isSameOrg && roleId === ROLE_IDS.manager) {
        return getOndemandUrl();
      }

      if (isSameOrg && roleId === ROLE_IDS.agent) {
        return getAgencyUrl();
      }

      return getUnifiedLoginUrlForRole({ email: userEmail || '', orgId: org.id, role: roleId });
    },
    [currentOrgId, userEmail],
  );

  const handleRoleSwitchClick = useCallback(
    (e: React.MouseEvent, org: IOrganization, roleId: RoleId) => {
      const isSameOrg = org.id === currentOrgId;

      if (!isSameOrg && !isSafeguardDisabled()) {
        e.preventDefault();
        onShowSwitchModal(org.id, roleId);
      } else {
        if (!isSameOrg && roleId === ROLE_IDS.admin) {
          clearLastVisitedRoute();
        }
        onClose();
      }
    },
    [currentOrgId, onClose, onShowSwitchModal],
  );

  if (loading) {
    return (
      <LoadingContainer>
        <Spin />
      </LoadingContainer>
    );
  }

  return (
    <OrgsContainer data-cy="org-switcher-orgs-list">
      {sortedOrganizations.map(org => {
        const isCurrentOrg = org.id === currentOrgId;
        const isExpanded = expandedOrgId === org.id;

        return (
          <OrgItem key={org.id} $isExpanded={isExpanded}>
            <OrgHeader
              $isExpanded={isExpanded}
              onClick={() => handleToggleExpand(org.id)}
              data-cy={`org-switcher-org-${org.id}`}
            >
              <OrgLogo $logoUrl={org.logo_url} />
              <OrgName>{org.name}</OrgName>
              <ChevronIcon className="fas fa-chevron-down" $isExpanded={isExpanded} />
            </OrgHeader>

            <RolesContainer $isExpanded={isExpanded}>
              {org.roles.map(role => {
                // Admin role in current org = current state (Dash)
                const isCurrent = isCurrentOrg && role.id === ROLE_IDS.admin;
                const switchUrl = getRoleSwitchUrl(org, role.id);

                return (
                  <RoleItem key={role.id} data-cy={`org-switcher-role-${org.id}-${role.id}`}>
                    <RoleName>{role.name}</RoleName>
                    {isCurrent ? (
                      <RoleStatus as="span" $isCurrent>
                        {t('dash.topbar.org_switcher.current')}
                      </RoleStatus>
                    ) : (
                      <RoleStatus
                        as="a"
                        href={switchUrl || '#'}
                        {...((role.id === ROLE_IDS.manager || role.id === ROLE_IDS.agent) && {
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        })}
                        onClick={e => handleRoleSwitchClick(e, org, role.id)}
                      >
                        {t('dash.topbar.org_switcher.switch')}
                      </RoleStatus>
                    )}
                  </RoleItem>
                );
              })}
            </RolesContainer>
          </OrgItem>
        );
      })}
    </OrgsContainer>
  );
};

export default OrgList;
